import React, { useState, useEffect } from 'react';
import { createMasterplan, MasterplanCreateData, generateMasterplanStreams, getMasterplan, getProject, Project } from '../services/api';
import StreamGenerationProgress from './StreamGenerationProgress';
import { downloadMasterplanHTML } from '../utils/masterplanHtmlExport';
import './MasterplanWizard.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface MasterplanWizardProps {
    projectId: string;
    onClose: () => void;
    onComplete: (masterplanId: string) => void;
}

interface WizardData {
    industry: string;
    business_type: string;
    primary_location: string;
    jurisdiction: string;
    country: string;
    num_streams: number;
    stream_names: string[];
    target_audience: string[];
    competitor_sources: string[];
    existing_research: string;
}

const MasterplanWizard: React.FC<MasterplanWizardProps> = ({ projectId, onClose, onComplete }) => {
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [currentStreamIndex, setCurrentStreamIndex] = useState(0);
    const [createdMasterplanId, setCreatedMasterplanId] = useState<string | null>(null);
    const [project, setProject] = useState<Project | null>(null);

    useEffect(() => {
        if (projectId) {
            getProject(projectId).then(setProject).catch(() => {});
        }
    }, [projectId]);

    // Derive placeholders from project client_info
    const ci = (project?.client_info ?? {}) as Record<string, string>;
    const industryHint = ci.industry || '';
    const locationHint = ci.location || '';
    const audienceHint = ci.target_customer || '';

    const [wizardData, setWizardData] = useState<WizardData>({
        industry: '',
        business_type: '',
        primary_location: '',
        jurisdiction: '',
        country: '',
        num_streams: 5,
        stream_names: [],
        target_audience: [],
        competitor_sources: [],
        existing_research: ''
    });

    // Temporary state for array inputs
    const [streamNameInput, setStreamNameInput] = useState('');
    const [audienceInput, setAudienceInput] = useState('');
    const [competitorInput, setCompetitorInput] = useState('');

    const totalSteps = 6;

    const handleNext = () => {
        // Validation for required fields based on step
        if (step === 1 && (!wizardData.industry || !wizardData.business_type)) {
            setError('Please fill in all required fields');
            return;
        }
        if (step === 2 && (!wizardData.primary_location || !wizardData.jurisdiction || !wizardData.country)) {
            setError('Please fill in all required fields');
            return;
        }
        if (step === 3 && (!wizardData.num_streams || wizardData.num_streams < 1 || wizardData.num_streams > 10)) {
            setError('Number of streams must be between 1 and 10');
            return;
        }
        if (step === 4 && wizardData.stream_names.length !== wizardData.num_streams) {
            setError(`Please define exactly ${wizardData.num_streams} stream name(s)`);
            return;
        }

        setError(null);
        setStep(step + 1);
    };

    const handleBack = () => {
        setError(null);
        setStep(step - 1);
    };

    const handleAddStreamName = () => {
        if (streamNameInput.trim() && wizardData.stream_names.length < wizardData.num_streams) {
            setWizardData({
                ...wizardData,
                stream_names: [...wizardData.stream_names, streamNameInput.trim()]
            });
            setStreamNameInput('');
        }
    };

    const handleRemoveStreamName = (index: number) => {
        setWizardData({
            ...wizardData,
            stream_names: wizardData.stream_names.filter((_, i) => i !== index)
        });
    };

    const handleAddAudience = () => {
        if (audienceInput.trim()) {
            setWizardData({
                ...wizardData,
                target_audience: [...wizardData.target_audience, audienceInput.trim()]
            });
            setAudienceInput('');
        }
    };

    const handleRemoveAudience = (index: number) => {
        setWizardData({
            ...wizardData,
            target_audience: wizardData.target_audience.filter((_, i) => i !== index)
        });
    };

    const handleAddCompetitor = () => {
        if (competitorInput.trim()) {
            setWizardData({
                ...wizardData,
                competitor_sources: [...wizardData.competitor_sources, competitorInput.trim()]
            });
            setCompetitorInput('');
        }
    };

    const handleRemoveCompetitor = (index: number) => {
        setWizardData({
            ...wizardData,
            competitor_sources: wizardData.competitor_sources.filter((_, i) => i !== index)
        });
    };

    const handleExport = async () => {
        if (!createdMasterplanId) return;

        try {
            // Fetch the latest masterplan data with all streams
            const masterplan = await getMasterplan(createdMasterplanId);
            downloadMasterplanHTML(masterplan);
        } catch (err: any) {
            setError('Failed to export masterplan');
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);

        try {
            // Step 1: Create masterplan
            const masterplanData: MasterplanCreateData = {
                project_id: projectId,
                industry: wizardData.industry,
                business_type: wizardData.business_type,
                primary_location: wizardData.primary_location,
                jurisdiction: wizardData.jurisdiction,
                country: wizardData.country,
                num_streams: wizardData.num_streams,
                target_audience: wizardData.target_audience.length > 0 ? wizardData.target_audience : undefined,
                competitor_sources: wizardData.competitor_sources.length > 0 ? wizardData.competitor_sources : undefined,
                existing_research: wizardData.existing_research || undefined
            };

            const masterplan = await createMasterplan(projectId, masterplanData);
            setCreatedMasterplanId(masterplan.masterplan_id);
            setIsSubmitting(false);

            // Step 2: Start stream generation with progress tracking
            setIsGenerating(true);
            setCurrentStreamIndex(0);

            try {
                // Generate all streams — progress jumps to 100% on completion
                const result = await generateMasterplanStreams(
                    masterplan.masterplan_id,
                    wizardData.stream_names
                );

                setCurrentStreamIndex(wizardData.stream_names.length);
                setIsGenerating(false);

                // Keep the progress UI visible for export
                // Don't auto-close - let user export first
            } catch (genErr: any) {
                setError(getApiError(genErr, 'Failed to generate streams'));
                setIsGenerating(false);
            }
        } catch (err: any) {
            setError(getApiError(err, 'Failed to create masterplan'));
            setIsSubmitting(false);
        }
    };

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                    <div className="wizard-step">
                        <h3>Business Context</h3>
                        <p className="step-description">Tell us about your business or industry</p>

                        <div className="form-group">
                            <label htmlFor="industry">Industry <span className="required">*</span></label>
                            <input
                                id="industry"
                                type="text"
                                value={wizardData.industry}
                                onChange={(e) => setWizardData({ ...wizardData, industry: e.target.value })}
                                placeholder={industryHint ? `e.g., ${industryHint}` : 'e.g., Legal services, Healthcare, Financial services'}
                                className="wizard-input"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="business_type">Business Type <span className="required">*</span></label>
                            <input
                                id="business_type"
                                type="text"
                                value={wizardData.business_type}
                                onChange={(e) => setWizardData({ ...wizardData, business_type: e.target.value })}
                                placeholder={industryHint ? `e.g., ${industryHint} practice, ${industryHint} consultancy` : 'e.g., Estate planning, Medical practice, Wealth management'}
                                className="wizard-input"
                            />
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="wizard-step">
                        <h3>Geographic Focus</h3>
                        <p className="step-description">Where is your primary market?</p>

                        <div className="form-group">
                            <label htmlFor="primary_location">City/Metro Area <span className="required">*</span></label>
                            <input
                                id="primary_location"
                                type="text"
                                value={wizardData.primary_location}
                                onChange={(e) => setWizardData({ ...wizardData, primary_location: e.target.value })}
                                placeholder={locationHint ? `e.g., ${locationHint}` : 'e.g., Melbourne, Sydney, Brisbane'}
                                className="wizard-input"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="jurisdiction">State/Province <span className="required">*</span></label>
                            <input
                                id="jurisdiction"
                                type="text"
                                value={wizardData.jurisdiction}
                                onChange={(e) => setWizardData({ ...wizardData, jurisdiction: e.target.value })}
                                placeholder="e.g., Victoria, NSW, Queensland"
                                className="wizard-input"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="country">Country <span className="required">*</span></label>
                            <input
                                id="country"
                                type="text"
                                value={wizardData.country}
                                onChange={(e) => setWizardData({ ...wizardData, country: e.target.value })}
                                placeholder="e.g., Australia, United States, United Kingdom"
                                className="wizard-input"
                            />
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="wizard-step">
                        <h3>Content Streams</h3>
                        <p className="step-description">How many content streams (practice areas/service lines) do you want?</p>

                        <div className="form-group">
                            <label htmlFor="num_streams">Number of Streams <span className="required">*</span></label>
                            <input
                                id="num_streams"
                                type="number"
                                min="1"
                                max="10"
                                value={wizardData.num_streams}
                                onChange={(e) => setWizardData({ ...wizardData, num_streams: parseInt(e.target.value) || 1 })}
                                className="wizard-input"
                            />
                            <small className="helper-text">
                                Recommended: 3-5 streams for focused execution. Maximum: 10 streams.
                            </small>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="wizard-step">
                        <h3>Define Content Streams</h3>
                        <p className="step-description">Name each of your {wizardData.num_streams} content streams (e.g., practice areas or service lines)</p>

                        <div className="form-group">
                            <label>Add Stream Name</label>
                            <div className="array-input-group">
                                <input
                                    type="text"
                                    value={streamNameInput}
                                    onChange={(e) => setStreamNameInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddStreamName())}
                                    placeholder={industryHint ? `e.g., ${industryHint} Strategy, ${industryHint} Compliance, Growth` : 'e.g., Estate Planning, Family Law, Corporate Law'}
                                    className="wizard-input"
                                    disabled={wizardData.stream_names.length >= wizardData.num_streams}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddStreamName}
                                    className="add-button"
                                    disabled={wizardData.stream_names.length >= wizardData.num_streams}
                                >
                                    Add
                                </button>
                            </div>
                            <small className="helper-text">
                                Added {wizardData.stream_names.length} of {wizardData.num_streams} streams
                            </small>
                        </div>

                        {wizardData.stream_names.length > 0 && (
                            <div className="tags-container">
                                {wizardData.stream_names.map((streamName, index) => (
                                    <div key={index} className="tag">
                                        {streamName}
                                        <button type="button" onClick={() => handleRemoveStreamName(index)} className="tag-remove">×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 5:
                return (
                    <div className="wizard-step">
                        <h3>Target Audience</h3>
                        <p className="step-description">Who is your target audience for this content? (Optional)</p>

                        <div className="form-group">
                            <label>Add Audience Segment</label>
                            <div className="array-input-group">
                                <input
                                    type="text"
                                    value={audienceInput}
                                    onChange={(e) => setAudienceInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAudience())}
                                    placeholder={audienceHint ? `e.g., ${audienceHint}` : 'e.g., High-net-worth individuals, Small business owners'}
                                    className="wizard-input"
                                />
                                <button type="button" onClick={handleAddAudience} className="add-button">Add</button>
                            </div>
                        </div>

                        {wizardData.target_audience.length > 0 && (
                            <div className="tags-container">
                                {wizardData.target_audience.map((audience, index) => (
                                    <div key={index} className="tag">
                                        {audience}
                                        <button type="button" onClick={() => handleRemoveAudience(index)} className="tag-remove">×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 6:
                return (
                    <div className="wizard-step">
                        <h3>Competitive Intelligence</h3>
                        <p className="step-description">Add competitors or authoritative sources to analyze (Optional)</p>

                        <div className="form-group">
                            <label>Add Competitor/Source</label>
                            <div className="array-input-group">
                                <input
                                    type="text"
                                    value={competitorInput}
                                    onChange={(e) => setCompetitorInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCompetitor())}
                                    placeholder="e.g., competitor.com, industry authority"
                                    className="wizard-input"
                                />
                                <button type="button" onClick={handleAddCompetitor} className="add-button">Add</button>
                            </div>
                        </div>

                        {wizardData.competitor_sources.length > 0 && (
                            <div className="tags-container">
                                {wizardData.competitor_sources.map((source, index) => (
                                    <div key={index} className="tag">
                                        {source}
                                        <button type="button" onClick={() => handleRemoveCompetitor(index)} className="tag-remove">×</button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="form-group" style={{ marginTop: '20px' }}>
                            <label htmlFor="existing_research">Existing Keyword Research</label>
                            <textarea
                                id="existing_research"
                                value={wizardData.existing_research}
                                onChange={(e) => setWizardData({ ...wizardData, existing_research: e.target.value })}
                                placeholder="Paste any existing keyword research or SEO strategy documents..."
                                className="wizard-textarea"
                                rows={6}
                            />
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="masterplan-wizard-overlay">
            <div className="masterplan-wizard-modal">
                <div className="wizard-header">
                    <h2>Create Content Masterplan</h2>
                    <button onClick={onClose} className="close-button" disabled={isSubmitting}>×</button>
                </div>

                <div className="wizard-progress">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${(step / totalSteps) * 100}%` }}
                        ></div>
                    </div>
                    <div className="progress-text">Step {step} of {totalSteps}</div>
                </div>

                {error && (
                    <div className="wizard-error">
                        {error}
                    </div>
                )}

                <div className="wizard-content">
                    {isGenerating || (createdMasterplanId && currentStreamIndex === wizardData.num_streams) ? (
                        <StreamGenerationProgress
                            streamNames={wizardData.stream_names}
                            currentStreamIndex={currentStreamIndex}
                            totalStreams={wizardData.num_streams}
                            isGenerating={isGenerating}
                            error={error}
                            onExport={handleExport}
                        />
                    ) : (
                        renderStepContent()
                    )}
                </div>

                {!isGenerating && !(createdMasterplanId && currentStreamIndex === wizardData.num_streams) && (
                    <div className="wizard-footer">
                        <div className="wizard-actions">
                            {step > 1 && (
                                <button
                                    onClick={handleBack}
                                    className="wizard-button secondary"
                                    disabled={isSubmitting}
                                >
                                    Back
                                </button>
                            )}

                            <div style={{ flex: 1 }}></div>

                            {step < totalSteps ? (
                                <button
                                    onClick={handleNext}
                                    className="wizard-button primary"
                                    disabled={isSubmitting}
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    className="wizard-button primary"
                                    disabled={isSubmitting || isGenerating}
                                >
                                    {isSubmitting ? 'Creating Masterplan...' : 'Generate Masterplan'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MasterplanWizard;
